// src/components/SolContDigital.jsx

import React, { useId } from "react";
import { ArrowUpRight, Check, Globe } from "lucide-react";

/* ======================================================
   CONFIGURACIÓN
====================================================== */

const DISPLAY_FONT = {
  fontFamily: '"Arial Narrow", "Roboto Condensed", Arial, sans-serif',
  fontStretch: "condensed",
};

const GRID_BACKGROUND = {
  backgroundImage: `
    linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)
  `,
  backgroundSize: "64px 64px",
};


/* ======================================================
   RESPONSIVO — MISMA ESCALA QUE MAINSCREEN Y ABOUTSCREEN

   Estilos locales; no modifican el navbar ni otras pantallas.
   Las tarjetas usan el ancho real de su contenedor.
   No fijamos alturas de texto ni añadimos scroll interno.
====================================================== */

const RESPONSIVE_STYLES = `
  .solcont-digital {
    --scd-section-space: clamp(2.5rem, 6vh, 5rem);
    --scd-stack: clamp(1rem, 2.5vh, 1.75rem);
    --scd-gap: clamp(1.5rem, 3vw, 3rem);
    --scd-panel: clamp(1.125rem, 2vw, 1.625rem);
    --scd-foreign-panel: clamp(1.25rem, 2.5vw, 2rem);
  }
  .solcont-digital .scd-container {
    box-sizing: border-box;
    width: 100%;
    max-width: 75rem;
    margin-inline: auto;
    padding-inline: clamp(1rem, 3vw, 2.5rem);
    padding-block: var(--scd-section-space);
  }
  .solcont-digital .scd-header {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--scd-gap);
  }
  .solcont-digital .scd-accent { margin-top: 1rem; }
  .solcont-digital .scd-spaced { margin-top: var(--scd-stack); }
  .solcont-digital .scd-title {
    margin-top: var(--scd-stack);
    font-size: clamp(1.875rem, 5vw, 3.25rem);
    line-height: 1.06;
    text-wrap: balance;
    overflow-wrap: anywhere;
  }
  .solcont-digital .scd-copy { font-size: 1rem; line-height: 1.6; }
  .solcont-digital .scd-languages { gap: 0.5rem; margin-top: 0.75rem; }
  .solcont-digital .scd-language {
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    max-width: 100%;
  }
  .solcont-digital .scd-services { margin-top: var(--scd-gap); }
  .solcont-digital .scd-services-label { margin-bottom: 1rem; }
  .solcont-digital .scd-services-grid {
    gap: 1rem;
    grid-template-columns: minmax(0, 1fr);
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 17rem), 1fr));
  }
  .solcont-digital .scd-service-card { padding: var(--scd-panel); }
  .solcont-digital .scd-service-header { margin-bottom: 1rem; gap: 0.75rem; }
  .solcont-digital .scd-service-number { width: 2.25rem; height: 2.25rem; }
  .solcont-digital .scd-service-title {
    font-size: clamp(1.125rem, 1.8vw, 1.375rem);
    line-height: 1.2;
    overflow-wrap: anywhere;
  }
  .solcont-digital .scd-service-copy {
    margin-top: 0.75rem;
    font-size: 0.875rem;
    line-height: 1.6;
  }
  .solcont-digital .scd-foreign { margin-top: var(--scd-gap); }
  .solcont-digital .scd-foreign-grid { grid-template-columns: minmax(0, 1fr); }
  .solcont-digital .scd-foreign-panel { padding: var(--scd-foreign-panel); }
  .solcont-digital .scd-foreign-title {
    margin-top: var(--scd-stack);
    font-size: clamp(1.5rem, 2.8vw, 2rem);
    line-height: 1.12;
    text-wrap: balance;
    overflow-wrap: anywhere;
  }
  .solcont-digital .scd-foreign-copy {
    margin-top: var(--scd-stack);
    font-size: 0.875rem;
    line-height: 1.6;
  }
  .solcont-digital .scd-button {
    min-height: 2.75rem;
    max-width: 100%;
    padding: 0.75rem 1.25rem;
    line-height: 1.4;
    text-align: center;
    white-space: normal;
  }
  .solcont-digital .scd-cta-note { margin-top: 0.75rem; line-height: 1.6; }
  .solcont-digital .scd-benefits { margin-top: 1rem; }
  .solcont-digital .scd-benefit { padding-block: 0.625rem; gap: 0.75rem; }
  .solcont-digital .scd-benefit:first-child { padding-top: 0; }
  .solcont-digital .scd-benefit-text {
    min-width: 0;
    font-size: 0.875rem;
    line-height: 1.6;
    overflow-wrap: anywhere;
  }
  .solcont-digital .scd-note { margin-top: 1rem; padding-top: 1rem; line-height: 1.6; }
  .solcont-digital .scd-closing { margin-top: var(--scd-gap); padding-top: 1.25rem; }
  .solcont-digital .scd-closing > p { min-width: 0; overflow-wrap: anywhere; }

  @supports (height: 100svh) {
    .solcont-digital {
      --scd-section-space: clamp(2.5rem, 6svh, 5rem);
      --scd-stack: clamp(1rem, 2.5svh, 1.75rem);
    }
  }

  @media (min-width: 60rem) {
    .solcont-digital .scd-header,
    .solcont-digital .scd-foreign-grid {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    }
    .solcont-digital .scd-header { align-items: end; }
    .solcont-digital .scd-title {
      font-size: clamp(2rem, min(3.8vw, 6vh), 3.25rem);
    }
    .solcont-digital .scd-benefits-panel { border-top: 0; border-left-width: 1px; }
    @supports (height: 100svh) {
      .solcont-digital .scd-title {
        font-size: clamp(2rem, min(3.8vw, 6svh), 3.25rem);
      }
    }
  }

  @media (min-width: 60rem) and (max-height: 46.25rem) {
    .solcont-digital {
      --scd-section-space: 2rem;
      --scd-stack: 1rem;
      --scd-gap: 1.5rem;
      --scd-panel: 1.125rem;
      --scd-foreign-panel: 1.25rem;
    }
    .solcont-digital .scd-service-number { width: 2rem; height: 2rem; }
    .solcont-digital .scd-service-header { margin-bottom: 0.75rem; }
    .solcont-digital .scd-benefit { padding-block: 0.5rem; }
    .solcont-digital .scd-benefit:first-child { padding-top: 0; }
  }

  @media (max-width: 39.999rem) {
    .solcont-digital .scd-button { width: 100%; }
  }
`;

const LANGUAGES = [
  { code: "es", label: "Español" },
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
];

function normalizeLanguage(value) {
  if (typeof value !== "string") return "es";

  const code = value.trim().toLowerCase().split(/[-_]/)[0];

  return ["es", "en", "fr"].includes(code) ? code : "es";
}

/* ======================================================
   CONTENIDO TRILINGÜE
====================================================== */

const CONTENT = {
  es: {
    eyebrow: "SOLCONT / SERVICIOS DIGITALES",
    title: "Tu contador.",
    titleAccent: "Donde estés.",
    description:
      "Contabilidad y estrategia fiscal en México, con atención en línea. Procesos claros, comunicación cercana e información útil para tomar decisiones.",
    languagesLabel: "Te atendemos en",
    servicesLabel: "Soluciones para tu operación",
    services: [
      {
        id: "accounting",
        title: "Contabilidad mensual",
        description:
          "Registro de operaciones, conciliaciones y reportes para conocer la situación de tu negocio y mantener tu información organizada.",
      },
      {
        id: "tax",
        title: "Declaraciones fiscales",
        description:
          "Preparación y presentación de declaraciones mensuales, anuales y complementarias, conforme a tus obligaciones.",
      },
      {
        id: "payroll",
        title: "Nómina e IMSS",
        description:
          "Timbrado de nómina, gestión de SUA y SIPARE, y acompañamiento en obligaciones ante el IMSS e INFONAVIT.",
      },
      {
        id: "strategy",
        title: "Estrategia fiscal",
        description:
          "Revisión de tu operación, deducciones y proyecciones para tomar decisiones fiscales dentro del marco legal.",
      },
      {
        id: "business",
        title: "Inicio y estructura empresarial",
        description:
          "Orientación para tu alta fiscal y elección de estructura. Acompañamiento y coordinación de los trámites necesarios para comenzar.",
      },
      {
        id: "support",
        title: "Asesoría continua",
        description:
          "Comunicación por WhatsApp, correo o videollamada para resolver dudas y dar seguimiento a tu operación, según el servicio contratado.",
      },
    ],
    foreignEyebrow: "EXTRANJEROS EN MÉXICO",
    foreignTitle: "Un nuevo país.",
    foreignTitleAccent: "Claridad fiscal desde el inicio.",
    foreignDescription:
      "Si resides, trabajas o desarrollas un negocio en México, te ayudamos a entender tu situación fiscal y a organizar tus obligaciones locales.",
    foreignSupport:
      "Atención en español, inglés y francés para que el idioma no sea una barrera.",
    benefitsLabel: "Cómo podemos ayudarte",
    benefits: [
      "Evaluación de tu situación y orientación sobre el régimen fiscal aplicable.",
      "Acompañamiento para tramitar tu RFC y e.firma ante el SAT.",
      "Organización y seguimiento de tus obligaciones fiscales en México.",
      "Revisión de la posible aplicación de convenios para evitar la doble tributación.",
      "Explicación de procesos y documentación en tu idioma.",
      "Acompañamiento en la regularización de tu situación fiscal.",
    ],
    note:
      "El alcance se define después de revisar tu caso. La aplicación de convenios depende, entre otros factores, de tu residencia fiscal, el tipo de ingreso y los requisitos correspondientes. Este servicio es fiscal y contable; no sustituye asesoría migratoria.",
    cta: "Cuéntanos tu caso",
    ctaNote: "El primer paso es entender qué necesitas.",
    closing: "Contabilidad + estrategia fiscal",
  },

  en: {
    eyebrow: "SOLCONT / DIGITAL SERVICES",
    title: "Your accountant.",
    titleAccent: "Wherever you are.",
    description:
      "Accounting and tax strategy in Mexico, delivered online. Clear processes, personal communication and useful information to support your decisions.",
    languagesLabel: "We support you in",
    servicesLabel: "Support for your operations",
    services: [
      {
        id: "accounting",
        title: "Monthly accounting",
        description:
          "Transaction recording, reconciliations and reports to help you understand your business and keep your financial information organized.",
      },
      {
        id: "tax",
        title: "Tax returns",
        description:
          "Preparation and filing of monthly, annual and amended tax returns according to your obligations.",
      },
      {
        id: "payroll",
        title: "Payroll and IMSS",
        description:
          "Mexican payroll tax receipts, SUA and SIPARE administration, and support with IMSS and INFONAVIT obligations.",
      },
      {
        id: "strategy",
        title: "Tax strategy",
        description:
          "A review of your operations, deductions and projections to support informed tax decisions within the legal framework.",
      },
      {
        id: "business",
        title: "Business setup and structure",
        description:
          "Guidance on tax registration and business structure, with support and coordination for the procedures needed to get started.",
      },
      {
        id: "support",
        title: "Ongoing advice",
        description:
          "Communication by WhatsApp, email or video call to answer questions and follow up on your operations within the agreed service scope.",
      },
    ],
    foreignEyebrow: "FOREIGN NATIONALS IN MEXICO",
    foreignTitle: "A new country.",
    foreignTitleAccent: "Tax clarity from the start.",
    foreignDescription:
      "If you live, work or run a business in Mexico, we help you understand your tax situation and organize your local obligations.",
    foreignSupport:
      "Support in Spanish, English and French, so language does not become a barrier.",
    benefitsLabel: "How we can help",
    benefits: [
      "An assessment of your situation and guidance on the applicable tax regime.",
      "Support with obtaining your RFC tax ID and e.firma from SAT.",
      "Organization and follow-up of your tax obligations in Mexico.",
      "A review of whether a double-taxation treaty may apply.",
      "Explanations of procedures and documentation in your language.",
      "Support with bringing your Mexican tax affairs up to date.",
    ],
    note:
      "The scope is defined after reviewing your case. Treaty eligibility depends on factors including your tax residency, income type and applicable requirements. This is an accounting and tax service, not a substitute for immigration advice.",
    cta: "Tell us about your needs",
    ctaNote: "The first step is understanding your situation.",
    closing: "Accounting + tax strategy",
  },

  fr: {
    eyebrow: "SOLCONT / SERVICES NUMÉRIQUES",
    title: "Votre comptable.",
    titleAccent: "Où que vous soyez.",
    description:
      "Comptabilité et stratégie fiscale au Mexique, avec un accompagnement en ligne. Des processus clairs, un échange de proximité et des informations utiles pour vos décisions.",
    languagesLabel: "Nous vous accompagnons en",
    servicesLabel: "Des solutions pour votre activité",
    services: [
      {
        id: "accounting",
        title: "Comptabilité mensuelle",
        description:
          "Enregistrement des opérations, rapprochements et rapports pour comprendre la situation de votre entreprise et organiser vos informations financières.",
      },
      {
        id: "tax",
        title: "Déclarations fiscales",
        description:
          "Préparation et dépôt des déclarations mensuelles, annuelles et rectificatives, selon vos obligations.",
      },
      {
        id: "payroll",
        title: "Paie et IMSS",
        description:
          "Émission des justificatifs fiscaux de paie, gestion de SUA et SIPARE, et accompagnement pour les obligations auprès de l’IMSS et de l’INFONAVIT.",
      },
      {
        id: "strategy",
        title: "Stratégie fiscale",
        description:
          "Analyse de votre activité, de vos déductions et de vos prévisions pour éclairer vos décisions fiscales dans le respect du cadre légal.",
      },
      {
        id: "business",
        title: "Création et structure d’entreprise",
        description:
          "Conseils pour votre inscription fiscale et le choix de votre structure, avec accompagnement et coordination des démarches de démarrage.",
      },
      {
        id: "support",
        title: "Conseil et suivi",
        description:
          "Échanges par WhatsApp, courriel ou visioconférence pour répondre à vos questions et suivre votre activité, selon la prestation convenue.",
      },
    ],
    foreignEyebrow: "RESSORTISSANTS ÉTRANGERS AU MEXIQUE",
    foreignTitle: "Un nouveau pays.",
    foreignTitleAccent: "Une fiscalité claire dès le départ.",
    foreignDescription:
      "Vous vivez, travaillez ou développez une entreprise au Mexique ? Nous vous aidons à comprendre votre situation fiscale et à organiser vos obligations locales.",
    foreignSupport:
      "Un accompagnement en espagnol, en anglais et en français, pour que la langue ne soit pas un obstacle.",
    benefitsLabel: "Notre accompagnement",
    benefits: [
      "Évaluation de votre situation et conseils sur le régime fiscal applicable.",
      "Accompagnement pour obtenir votre RFC et votre e.firma auprès du SAT.",
      "Organisation et suivi de vos obligations fiscales au Mexique.",
      "Examen de l’application éventuelle d’une convention contre la double imposition.",
      "Explication des démarches et des documents dans votre langue.",
      "Accompagnement dans la régularisation de votre situation fiscale.",
    ],
    note:
      "Le périmètre de la prestation est défini après examen de votre dossier. L’application d’une convention dépend notamment de votre résidence fiscale, de la nature de vos revenus et des conditions requises. Ce service comptable et fiscal ne remplace pas un conseil en immigration.",
    cta: "Parlons de votre situation",
    ctaNote: "La première étape : comprendre vos besoins.",
    closing: "Comptabilité + stratégie fiscale",
  },
};

/* ======================================================
   ÉLÉMENTS VISUELS
====================================================== */

function AccentRule() {
  return (
    <span
      aria-hidden="true"
      className="block h-1 w-16 rounded-full bg-[#F72585]"
    />
  );
}

function BrandOrbits() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -right-48 -top-36 h-[560px] w-[560px] select-none"
    >
      <div className="absolute inset-0 rounded-full border border-white/20" />

      <div className="absolute inset-20 rounded-full border border-white/20" />

      <div
        className="absolute left-[72px] top-[72px] h-5 w-5 rounded-full bg-[#F72585]"
      />
    </div>
  );
}

function ServiceCard({ service, index }) {
  return (
    <article
      className="scd-service-card flex h-full min-w-0 flex-col rounded-2xl bg-[#F7F3E9] text-[#081B3E]"
    >
      <div className="scd-service-header flex items-center" aria-hidden="true">
        <span
          className="scd-service-number flex shrink-0 items-center justify-center rounded-full border border-[#1648EE]/20 text-xs font-bold tabular-nums text-[#1648EE]"
        >
          {String(index + 1).padStart(2, "0")}
        </span>

        <span className="h-px flex-1 bg-[#081B3E]/20" />

        <span className="h-2 w-2 shrink-0 rounded-full bg-[#F72585]" />
      </div>

      <h3
        className="scd-service-title font-bold tracking-tight"
        style={DISPLAY_FONT}
      >
        {service.title}
      </h3>

      <p className="scd-service-copy text-[#081B3E]/80">
        {service.description}
      </p>
    </article>
  );
}

/* ======================================================
   COMPONENTE
   El idioma lo proporciona AboutScreen.
====================================================== */

export default function SolContDigital({ language = "es" }) {
  const locale = normalizeLanguage(language);
  const t = CONTENT[locale];

  const sectionId = useId();
  const titleId = `${sectionId}-title`;
  const servicesId = `${sectionId}-services`;
  const foreignTitleId = `${sectionId}-foreign`;

  return (
    <section
      lang={locale}
      aria-labelledby={titleId}
      className="solcont-digital relative isolate overflow-hidden bg-[#1648EE] text-[#F7F3E9]"
    >
      <style>{RESPONSIVE_STYLES}</style>

      {/* Fondo decorativo: no intercepta clics ni recibe foco. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={GRID_BACKGROUND}
      />

      <BrandOrbits />

      <div className="scd-container relative">
        {/* ==============================================
            ENCABEZADO
        ============================================== */}

        <div className="scd-header grid">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/80">
              {t.eyebrow}
            </p>

            <div className="scd-accent">
              <AccentRule />
            </div>

            <h2
              id={titleId}
              className="scd-title font-black uppercase tracking-tight"
              style={DISPLAY_FONT}
            >
              <span className="block">{t.title}</span>
              <span className="mt-2 block">{t.titleAccent}</span>
            </h2>
          </div>

          <div className="min-w-0">
            <p className="scd-copy max-w-xl text-white/90">
              {t.description}
            </p>

            <div className="scd-spaced">
              <p className="flex items-center gap-2 text-sm font-medium text-white/80">
                <Globe
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0"
                />
                {t.languagesLabel}
              </p>

              {/* Informativos; el selector de idioma permanece en el Navbar. */}
              <ul className="scd-languages flex flex-wrap">
                {LANGUAGES.map((item) => (
                  <li
                    key={item.code}
                    className="scd-language inline-flex items-center rounded-full border border-white/25 bg-[#081B3E]/15"
                  >
                    <span
                      aria-hidden="true"
                      className="text-xs font-bold uppercase tracking-wider"
                    >
                      {item.code}
                    </span>

                    <span
                      lang={item.code}
                      className="text-sm text-white/90"
                    >
                      {item.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ==============================================
            SERVICIOS
        ============================================== */}

        <div className="scd-services">
          <p
            id={servicesId}
            className="scd-services-label text-xs font-semibold uppercase tracking-[0.16em] text-white/80"
          >
            {t.servicesLabel}
          </p>

          <ul
            aria-labelledby={servicesId}
            className="scd-services-grid grid"
          >
            {t.services.map((service, index) => (
              <li key={service.id} className="min-w-0">
                <ServiceCard service={service} index={index} />
              </li>
            ))}
          </ul>
        </div>

        {/* ==============================================
            EXTRANJEROS EN MÉXICO
        ============================================== */}

        <section
          aria-labelledby={foreignTitleId}
          className="scd-foreign overflow-hidden rounded-3xl border border-white/15 bg-[#081B3E]"
        >
          <div className="scd-foreign-grid grid">
            <div className="scd-foreign-panel min-w-0">
              <p
                className="text-xs font-semibold uppercase leading-6 tracking-[0.16em] text-white/75"
              >
                {t.foreignEyebrow}
              </p>

              <div className="scd-accent">
                <AccentRule />
              </div>

              <h3
                id={foreignTitleId}
                className="scd-foreign-title font-bold uppercase tracking-tight"
                style={DISPLAY_FONT}
              >
                <span className="block">{t.foreignTitle}</span>

                <span className="mt-2 block">
                  {t.foreignTitleAccent}
                </span>
              </h3>

              <p className="scd-foreign-copy text-white/85">
                {t.foreignDescription}
              </p>

              <p className="scd-foreign-copy font-medium text-[#F7F3E9]">
                {t.foreignSupport}
              </p>

              <div className="scd-spaced">
                <a
                  href="/#asesoria"
                  className="scd-button inline-flex items-center justify-center gap-3 rounded-xl bg-[#F72585] text-sm font-bold text-[#081B3E] transition-colors hover:bg-[#FF5CA5] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
                >
                  <span>{t.cta}</span>

                  <ArrowUpRight
                    aria-hidden="true"
                    className="h-5 w-5 shrink-0"
                  />
                </a>

                <p className="scd-cta-note text-xs text-white/70">
                  {t.ctaNote}
                </p>
              </div>
            </div>

            <div
              className="scd-foreign-panel scd-benefits-panel min-w-0 border-t border-white/15"
            >
              <h4
                className="text-xs font-semibold uppercase tracking-[0.16em] text-white/80"
              >
                {t.benefitsLabel}
              </h4>

              <ul className="scd-benefits divide-y divide-white/15">
                {t.benefits.map((benefit) => (
                  <li
                    key={benefit}
                    className="scd-benefit flex items-start"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F72585]"
                    >
                      <Check
                        className="h-3 w-3 text-[#081B3E]"
                        strokeWidth={3}
                      />
                    </span>

                    <span className="scd-benefit-text text-white/90">
                      {benefit}
                    </span>
                  </li>
                ))}
              </ul>

              <p className="scd-note border-t border-white/15 text-xs text-white/70">
                {t.note}
              </p>
            </div>
          </div>
        </section>

        {/* Firma editorial; no reconstruye el logotipo. */}
        <div
          className="scd-closing flex items-center gap-4 border-t border-white/20"
        >
          <span
            aria-hidden="true"
            className="h-2 w-2 shrink-0 rounded-full bg-[#F72585]"
          />

          <p className="text-xs font-medium uppercase tracking-[0.16em] text-white/80">
            {t.closing}
          </p>
        </div>
      </div>
    </section>
  );
}
